import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDraggable,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './FlowEditor.css'
import { PageHeader } from '../components/PageHeader'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'
import { FlowIcon } from '../components/FlowIcon'
import { useFlow } from '../hooks/useFlows'
import { useScenario } from '../hooks/useScenario'
import { useCommand } from '../hooks/useCommand'
import { useScenarioItems } from '../hooks/useScenarioItems'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { AccessDeniedBanner } from '../components/AccessDeniedBanner'
import type { ScenarioItem, FlowRanking } from '../../core/domain/models'
import { resolveFlowPreferenceStatus } from '../../core/domain/flowPreferenceStatus'
import { resolveFlowColor } from '../../core/domain/flowColors'
import { StarOutlineIcon, StarFilledIcon } from '../components/Icons'
import {
  arraysEqual,
  computeFlowRankingsPatchForAppendMany,
  computeFlowRankingsPatchForOrderedPosition,
  moveFlowItemBetweenContainers,
  type FlowDragContainers,
} from './flowEditorMultiContainer'

const ORDER_GAP = 100

// Helper for comparing rankings ignoring key order
function areRankingsEqual(
  a: Record<string, FlowRanking>,
  b: Record<string, FlowRanking>
): boolean {
  if (a === b) return true
  if (!a || !b) return false
  
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  
  if (keysA.length !== keysB.length) return false
  
  for (const key of keysA) {
    const valA = a[key]
    const valB = b[key]
    
    if (!valB) return false
    if (Math.abs(valA.r - valB.r) > 0.0001) return false // Float tolerance
    if (!!valA.notAvailable !== !!valB.notAvailable) return false
    if (valA.n !== valB.n) return false
  }
  
  return true
}

interface SortableItemProps {
  id: string;
  item: ScenarioItem;
  onToggleAvailability: (itemId: string) => void;
  onTogglePreferred?: (itemId: string) => void;
  showAvailabilityToggle?: boolean;
  isPreferred?: boolean;
}

function SortableItem({ id, item, onToggleAvailability, onTogglePreferred, showAvailabilityToggle, isPreferred }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={'sortable-item-wrapper'}
    >
      <div
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className={`item-card flow-item ${isDragging ? 'sortable-placeholder' : ''}`}
        style={{ touchAction: isDragging ? 'none' : 'pan-y' }}
      >
        {showAvailabilityToggle && onTogglePreferred && (
          <button
            className="flow-item-star-btn"
            onClick={(e) => {
              e.stopPropagation();
              onTogglePreferred(id);
            }}
            title={isPreferred ? "Remove preferred status" : "Mark as preferred"}
            aria-label={isPreferred ? "Remove preferred" : "Mark as preferred"}
          >
            {isPreferred ? <StarFilledIcon size={16} /> : <StarOutlineIcon size={16} />}
          </button>
        )}
        <div className="item-name">{item.name}</div>
        {showAvailabilityToggle && (
          <button
            className="flow-item-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              onToggleAvailability(id);
            }}
            title="Mark as unavailable"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

function UnavailableItem({ item, onToggleAvailability }: { item: ScenarioItem; onToggleAvailability: (itemId: string) => void }) {
  return (
    <div className="item-card flow-item flow-item-unavailable">
      <div className="item-name">{item.name}</div>
      <button
        className="flow-item-action-btn"
        onClick={() => onToggleAvailability(item.id)}
        title="Mark as available"
      >
        +
      </button>
    </div>
  );
}

function UnorderedItem({
  item,
  isSelected,
  onToggleSelected,
}: {
  item: ScenarioItem
  isSelected: boolean
  onToggleSelected: (id: string) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id: item.id })

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`item-card flow-item flow-item-unordered ${isSelected ? 'flow-item-selected' : ''} ${isDragging ? 'flow-item-dragging' : ''}`}
    >
      <label
        className="flow-item-select"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        title={isSelected ? 'Deselect' : 'Select'}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelected(item.id)}
        />
      </label>
      <div className="item-name">{item.name}</div>
    </div>
  )
}

function FlowContainerSection({
  title,
  count,
  containerId,
  itemIds,
  itemsById,
  onToggleAvailability,
  onTogglePreferred,
  showAvailabilityToggle,
  rankings,
  emptyMessage,
}: {
  title: string
  count: number
  containerId: 'unordered' | 'ordered'
  itemIds: string[]
  itemsById: Map<string, ScenarioItem>
  onToggleAvailability: (id: string) => Promise<void>
  onTogglePreferred?: (id: string) => Promise<void>
  showAvailabilityToggle: boolean
  rankings?: Record<string, FlowRanking>
  emptyMessage: string
}) {
  const { setNodeRef } = useDroppable({
    id: containerId,
  })

  return (
    <section className="flow-section">
      <h2 className="flow-section-title">{title} ({count})</h2>
      <div ref={setNodeRef} style={{ minHeight: '100px' }}>
        {itemIds.length === 0 ? (
          <div className="flow-section-empty">{emptyMessage}</div>
        ) : (
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <div className="flow-items-list">
              {itemIds.map((id) => {
                const item = itemsById.get(id)
                if (!item) return null
                const isPreferred = rankings ? resolveFlowPreferenceStatus(rankings[id]) === 'preferred' : false
                return (
                  <SortableItem
                    key={id}
                    id={id}
                    item={item}
                    onToggleAvailability={onToggleAvailability}
                    onTogglePreferred={onTogglePreferred}
                    showAvailabilityToggle={showAvailabilityToggle}
                    isPreferred={isPreferred}
                  />
                )
              })}
            </div>
          </SortableContext>
        )}
      </div>
    </section>
  )
}

export default function FlowEditor() {
  const { scenarioId, flowId } = useParams<{ scenarioId: string; flowId: string }>()
  const navigate = useNavigate()
  const { scenario, loading: scenarioLoading } = useScenario(scenarioId || '', { passive: true })
  const { flow, loading: flowLoading } = useFlow(scenarioId || '', flowId || '')
  const { items: scenarioItems } = useScenarioItems(scenarioId || '', { passive: true })
  const command = useCommand()

  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [dragContainers, setDragContainers] = useState<FlowDragContainers | null>(null)
  const [selectedUnorderedIds, setSelectedUnorderedIds] = useState<Set<string>>(() => new Set())
  
  const [metaItems, setMetaItems] = useState<ScenarioItem[]>([])
  const [optimisticRankings, setOptimisticRankings] = useState<Record<string, FlowRanking>>({})
  const lastSyncedFlowRankingsRef = useRef<Record<string, FlowRanking> | null>(null)

  useEffect(() => {
    if (scenarioItems) {
      setMetaItems(scenarioItems)
    }
  }, [scenarioItems])

  // Reset server-sync cursor when switching flows
  useEffect(() => {
    lastSyncedFlowRankingsRef.current = null
  }, [scenarioId, flowId])

  // Sync optimistic rankings from flow (server -> local), without stomping in-flight optimistic updates
  useEffect(() => {
    if (!flow) return

    const lastServerRankings = lastSyncedFlowRankingsRef.current
    if (lastServerRankings && areRankingsEqual(lastServerRankings, flow.rankings)) {
      return
    }

    const flowRankings = flow.rankings || {}
    lastSyncedFlowRankingsRef.current = flowRankings
    setOptimisticRankings(prev => (areRankingsEqual(prev, flowRankings) ? prev : flowRankings))
  }, [flow])

  useEffect(() => {
    if (!scenarioId || !flowId) {
      navigate('/')
    }
  }, [scenarioId, flowId, navigate])

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 300, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const canEdit = scenario?.role === 'owner' || scenario?.role === 'editor'

  const currentRankings = optimisticRankings || {}

  // Categorize items
  const orderedItems: ScenarioItem[] = []
  const unavailableItems: ScenarioItem[] = []
  const unorderedItems: ScenarioItem[] = []

  // Ensure metaItems is defined
  const itemsToProcess = metaItems || []

  for (const item of itemsToProcess) {
    const ranking = currentRankings[item.id]
    if (ranking) {
      if (ranking.notAvailable) {
        unavailableItems.push(item)
      } else {
        orderedItems.push(item)
      }
    } else {
      unorderedItems.push(item)
    }
  }

  // Sort ordered items by rank
  orderedItems.sort((a, b) => {
    const rankA = currentRankings[a.id]?.r ?? 0
    const rankB = currentRankings[b.id]?.r ?? 0
    return rankA - rankB
  })

  const orderedIds = orderedItems.map(i => i.id)
  const unorderedIds = unorderedItems.map(i => i.id)

  const itemsById = (() => {
    const map = new Map<string, ScenarioItem>()
    for (const item of itemsToProcess) {
      map.set(item.id, item)
    }
    return map
  })()

  const visibleContainers: FlowDragContainers = dragContainers ?? {
    unordered: unorderedIds,
    ordered: orderedIds,
  }

  // Keep selection consistent with currently visible unordered ids.
  // This also clears stale selections when items move to ordered.
  useEffect(() => {
    setSelectedUnorderedIds(prev => {
      if (prev.size === 0) return prev
      const next = new Set<string>()
      for (const id of prev) {
        if (visibleContainers.unordered.includes(id)) next.add(id)
      }
      return next.size === prev.size ? prev : next
    })
  }, [visibleContainers.unordered])

  if (scenarioLoading || flowLoading) {
    return <LoadingSpinner />
  }

  if (!scenario || !flow) {
    return <div>Flow not found</div>
  }


  const handleDragStart = (event: DragStartEvent) => {
    const activeId = event.active.id as string
    setActiveDragId(activeId)
    setDragContainers({
      unordered: unorderedIds,
      ordered: orderedIds,
    })
  }

  const toggleUnorderedSelected = (id: string) => {
    setSelectedUnorderedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAllUnordered = () => {
    setSelectedUnorderedIds(new Set(visibleContainers.unordered))
  }

  const clearUnorderedSelection = () => {
    setSelectedUnorderedIds(new Set())
  }

  const addSelectedToOrdered = async () => {
    if (!scenarioId || !flowId || !canEdit) return

    const idsToAdd = visibleContainers.unordered.filter(id => selectedUnorderedIds.has(id))
    if (idsToAdd.length === 0) return

    const itemNames: Record<string, string> = {}
    idsToAdd.forEach(id => {
      itemNames[id] = itemsById.get(id)?.name || ''
    })

    const patch = computeFlowRankingsPatchForAppendMany({
      rankings: currentRankings,
      orderedIds,
      idsToAppend: idsToAdd,
      itemNames,
      orderGap: ORDER_GAP,
    })

    if (Object.keys(patch).length === 0) {
      clearUnorderedSelection()
      return
    }

    setOptimisticRankings(prev => ({
      ...prev,
      ...patch,
    }))

    try {
      await command.updateFlowRankings(scenarioId, flowId, patch)
      clearUnorderedSelection()
    } catch (error) {
      console.error('Error updating flow rankings:', error)
      setOptimisticRankings(flow?.rankings || {})
    }
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    const overId = over?.id as string | undefined
    if (!overId) return

    const activeId = active.id as string

    setDragContainers(prev => {
      const base = prev ?? { unordered: unorderedIds, ordered: orderedIds }
      const next = moveFlowItemBetweenContainers({
        containers: base,
        activeId,
        overId,
        allowMoveFromOrderedToUnordered: false,
      })
      return next
    })
  }

  const handleDragCancel = () => {
    setActiveDragId(null)
    setDragContainers(null)
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    setActiveDragId(null)
    const capturedContainers = dragContainers
    setDragContainers(null)

    if (!over || !scenarioId || !flowId || !canEdit) return

    const activeId = active.id as string
    const finalContainers: FlowDragContainers =
      capturedContainers ?? { unordered: unorderedIds, ordered: orderedIds }

    // If ordered list didn't change, don't emit a command.
    if (arraysEqual(finalContainers.ordered, orderedIds)) {
      return
    }

    const patch = computeFlowRankingsPatchForOrderedPosition({
      rankings: currentRankings,
      orderedIdsAfter: finalContainers.ordered,
      activeId,
      itemName: itemsById.get(activeId)?.name,
      orderGap: ORDER_GAP,
    })

    if (!patch) return

    setOptimisticRankings(prev => ({
      ...prev,
      ...patch,
    }))

    try {
      await command.updateFlowRankings(scenarioId, flowId, patch)
    } catch (error) {
      console.error('Error updating flow rankings:', error)
      setOptimisticRankings(flow?.rankings || {})
    }
  }

  /*
  const handleAddToFlow = async (itemId: string) => {
    if (!scenarioId || !flowId || !canEdit) return

    try {
      // Add to end of ordered list
      const maxRank = orderedItems.length > 0
        ? Math.max(...orderedItems.map((i: ScenarioItem) => currentRankings[i.id]?.r || 0))
        : 0
      const newRank = maxRank + ORDER_GAP

      // Optimistic update
      setOptimisticRankings(prev => ({
        ...prev,
        [itemId]: { r: newRank, notAvailable: false }
      }))

      await command.updateFlowRankings(scenarioId, flowId, {
        [itemId]: { r: newRank, notAvailable: false }
      })
    } catch (error) {
      console.error('Error adding item to flow:', error)
      // Revert on error
      setOptimisticRankings(flow.rankings)
    }
  }
  */

  const handleToggleAvailability = async (itemId: string) => {
    if (!scenarioId || !flowId || !canEdit) return

    try {
      const ranking = currentRankings[itemId]
      if (!ranking) return

      const newNotAvailable = !ranking.notAvailable
      // When making unavailable, reset preferred status
      const newStatus = newNotAvailable ? 'notAvailable' : 'normal'

      // Optimistic update
      setOptimisticRankings(prev => ({
        ...prev,
        [itemId]: { r: ranking.r, notAvailable: newNotAvailable, n: ranking.n || itemsById.get(itemId)?.name, status: newStatus }
      }))

      await command.setItemFlowPreferences(scenarioId, itemId, {
        [flowId]: { status: newStatus }
      })
    } catch (error) {
      console.error('Error toggling availability:', error)
      // Revert on error
      setOptimisticRankings(flow?.rankings || {})
    }
  }

  const handleTogglePreferred = async (itemId: string) => {
    if (!scenarioId || !flowId || !canEdit) return

    try {
      const ranking = currentRankings[itemId]
      if (!ranking) return

      const currentStatus = resolveFlowPreferenceStatus(ranking)
      const newStatus = currentStatus === 'preferred' ? 'normal' : 'preferred'

      // Optimistic update
      setOptimisticRankings(prev => ({
        ...prev,
        [itemId]: { r: ranking.r, notAvailable: false, n: ranking.n || itemsById.get(itemId)?.name, status: newStatus }
      }))

      await command.setItemFlowPreferences(scenarioId, itemId, {
        [flowId]: { status: newStatus }
      })
    } catch (error) {
      console.error('Error toggling preferred:', error)
      // Revert on error
      setOptimisticRankings(flow?.rankings || {})
    }
  }

  const activeDragItem = activeDragId ? metaItems.find((i: ScenarioItem) => i.id === activeDragId) : null

  return (
    <div className="flow-editor-container">
      <PageHeader
        kind="Flow"
        kindIcon={
          <span style={{ color: `var(--flow-accent-${resolveFlowColor(flow)})` }}>
            <FlowIcon size={14} />
          </span>
        }
        tone="flow"
        title={flow.name}
        subtitle={flow.description || ''}
        onBack={() => navigate(-1)}
      />

      <PwaUpdateBanner />

      {!canEdit && <AccessDeniedBanner />}

      <div className="flow-editor-content">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {/* Unavailable Items Section - Only show if there are unavailable items */}
          {unavailableItems.length > 0 && (
            <section className="flow-section">
              <h2 className="flow-section-title">Unavailable Items ({unavailableItems.length})</h2>
              <div className="flow-items-list">
                {unavailableItems.map((item: ScenarioItem) => (
                  <UnavailableItem
                    key={item.name}
                    item={item}
                    onToggleAvailability={handleToggleAvailability}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="flow-section">
            <h2 className="flow-section-title">Unordered Items ({visibleContainers.unordered.length})</h2>
            {visibleContainers.unordered.length === 0 ? (
              <div className="flow-section-empty">All items are ordered or marked as unavailable.</div>
            ) : (
              <>
                <div className="flow-unordered-toolbar">
                  <div className="flow-unordered-toolbar-left">
                    <span className="flow-unordered-selected-count">
                      {selectedUnorderedIds.size} selected
                    </span>
                  </div>
                  <div className="flow-unordered-toolbar-actions">
                    <button
                      className="btn"
                      type="button"
                      onClick={selectAllUnordered}
                      disabled={visibleContainers.unordered.length === 0}
                    >
                      Select all
                    </button>
                    <button
                      className="btn"
                      type="button"
                      onClick={clearUnorderedSelection}
                      disabled={selectedUnorderedIds.size === 0}
                    >
                      Clear
                    </button>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={addSelectedToOrdered}
                      disabled={selectedUnorderedIds.size === 0}
                    >
                      Add selected to ordered
                    </button>
                  </div>
                </div>

                <div className="flow-items-list">
                  {visibleContainers.unordered.map((id) => {
                    const item = itemsById.get(id)
                    if (!item) return null
                    return (
                      <UnorderedItem
                        key={id}
                        item={item}
                        isSelected={selectedUnorderedIds.has(id)}
                        onToggleSelected={toggleUnorderedSelected}
                      />
                    )
                  })}
                </div>
              </>
            )}
          </section>

          <FlowContainerSection
            title="Ordered Items"
            count={visibleContainers.ordered.length}
            containerId="ordered"
            itemIds={visibleContainers.ordered}
            itemsById={itemsById}
            onToggleAvailability={handleToggleAvailability}
            onTogglePreferred={handleTogglePreferred}
            showAvailabilityToggle={true}
            rankings={currentRankings}
            emptyMessage="No items ordered yet. Add items from the unordered section above."
          />

          <DragOverlay>
            {activeDragItem ? (
              <div className="item-card flow-item flow-item-overlay">
                <div className="item-name">{activeDragItem.name}</div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  )
}
